import {
  InfoCircleOutlined,
  UploadOutlined,
  LoadingOutlined,
  FileExcelOutlined,
  DownloadOutlined,
  StepForwardOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Row,
  Skeleton,
  Space,
  Spin,
} from "antd";
import { UploadRequestOption } from "rc-upload/lib/interface";
import Dragger from "antd/es/upload/Dragger";
import { useCallback, useEffect, useMemo, useState } from "react";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import dayjs from "dayjs";
import {
  HistoricalDataFileResponse,
  HistoricalDataFilesStatusResponse,
} from "api/generated";
import {
  UPLOAD_KIND,
  HISTORICAL_DATA_TYPE,
  useHistoricalData,
} from "hooks/historicalData/useHistoricalData";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);
dayjs.locale("es");

interface UploadStepProps {
  kindSelected: UPLOAD_KIND;
  selectedMonth: dayjs.Dayjs | undefined;
  handleUpload: (
    fileContent: string,
    label: HISTORICAL_DATA_TYPE
  ) => Promise<HistoricalDataFilesStatusResponse | undefined>;
  existingFiles: HISTORICAL_DATA_TYPE[];
  downloadHistoricalLayoutFile: (label: HISTORICAL_DATA_TYPE) => void;
  deleteLayoutFile: (label: HISTORICAL_DATA_TYPE) => void;
  areAllFilesUploaded: boolean;
  executeValidationsAndContinue: () => void;
  uploadedFiles: HistoricalDataFileResponse[];
}

function UploadStep({
  handleUpload,
  kindSelected,
  selectedMonth,
  existingFiles,
  uploadedFiles,
  downloadHistoricalLayoutFile,
  areAllFilesUploaded,
  executeValidationsAndContinue,
  deleteLayoutFile,
}: UploadStepProps) {
  const {
    FILE_RULES,
    DISTRIBUTION_POINT_KINDS,
    transformFileForUpload,
    validateFileBeforeUpload,
    formatMonthYear,
  } = useHistoricalData();

  const [uploadingStates, setUploadingStates] = useState<
    Record<HISTORICAL_DATA_TYPE, boolean>
  >({
    [HISTORICAL_DATA_TYPE.VENTAS]: false,
    [HISTORICAL_DATA_TYPE.TRASPASOS_ENTRADA]: false,
    [HISTORICAL_DATA_TYPE.INVENTARIOS]: false,
    [HISTORICAL_DATA_TYPE.COMPRAS]: false,
    [HISTORICAL_DATA_TYPE.TRASPASOS_SALIDA]: false,
    [HISTORICAL_DATA_TYPE.PRE_VALIDATION_SUMMARY]: false,
    [HISTORICAL_DATA_TYPE.DIFFERENCES_FILE]: false,
    [HISTORICAL_DATA_TYPE.JSONS_SUMMARY]: false,
    [HISTORICAL_DATA_TYPE.JSONS_ZIP]: false,
  });
  const [currentTime, setCurrentTime] = useState(dayjs());

  const requiredFiles = useMemo(() => {
    return FILE_RULES.filter((rule) => rule.requiredFor.includes(kindSelected));
  }, [kindSelected, FILE_RULES]);

  const monthToString = useMemo(
    () => formatMonthYear(selectedMonth),
    [selectedMonth, formatMonthYear]
  );

  const showUploadSection = useMemo(
    () => selectedMonth != undefined,
    [selectedMonth]
  );

  const handleFileUpload = useCallback(
    async (options: UploadRequestOption, type: HISTORICAL_DATA_TYPE) => {
      try {
        setUploadingStates((prev) => ({ ...prev, [type]: true }));
        await transformFileForUpload(options, type, handleUpload);
      } finally {
        setUploadingStates((prev) => ({ ...prev, [type]: false }));
      }
    },
    [transformFileForUpload, handleUpload]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs());
    }, 10000); // Actualiza marca temporal cada 10 segundos

    return () => clearInterval(timer);
  }, []);

  return (
    <div>
      <Divider />

      <Row gutter={24}>
        <ResponsiveCol size="xl">
          <strong
            style={{
              width: "100%",
              gap: "5px",
              display: "flex",
              textWrap: "wrap",
              paddingTop: "18px",
            }}
          >
            <InfoCircleOutlined />
            {kindSelected === UPLOAD_KIND.PLANTAS &&
              DISTRIBUTION_POINT_KINDS[0].label}
            {kindSelected === UPLOAD_KIND.ESTACIONES &&
              DISTRIBUTION_POINT_KINDS[1].label}
          </strong>
        </ResponsiveCol>

        <ResponsiveCol size="xl">
          <p style={{ fontSize: "12px" }}>
            {kindSelected === UPLOAD_KIND.PLANTAS &&
              DISTRIBUTION_POINT_KINDS[0].description}
            {kindSelected === UPLOAD_KIND.ESTACIONES &&
              DISTRIBUTION_POINT_KINDS[1].description}
          </p>
        </ResponsiveCol>

        <ResponsiveCol>
          <strong>Período de validación</strong>
        </ResponsiveCol>

        <ResponsiveCol size="xl">
          {monthToString} - Todas las fechas de los archivos deben estar dentro
          de este mes.
        </ResponsiveCol>

        <ResponsiveCol>
          <strong>Archivos requeridos:</strong>
        </ResponsiveCol>

        <ResponsiveCol size="xl">
          <ul>
            {requiredFiles.map((item) => (
              <li key={item.label}>{item.label}</li>
            ))}
          </ul>
        </ResponsiveCol>
      </Row>

      <Divider />
      {!showUploadSection && (
        <Card
          title={
            <Alert
              message="Selección requerida"
              description="Selecciona un periodo de validación para habilitar la carga de archivos"
              type="warning"
              showIcon
            />
          }
        >
          <Skeleton paragraph={{ rows: 4 }} active />
        </Card>
      )}

      {showUploadSection && (
        <Row gutter={24}>
          {requiredFiles.map((file, index) => {
            const isUploaded = existingFiles.includes(file.type);
            const uploadedFile = uploadedFiles.find((f) => {
              return f.type === file.type;
            });
            return (
              <ResponsiveCol key={index}>
                <Form.Item
                  label={file.label}
                  extra={
                    isUploaded ? (
                      <div>
                        <Row>
                          <Space>
                            {file.description}
                            <Button
                              type="primary"
                              icon={<DownloadOutlined />}
                              onClick={() =>
                                downloadHistoricalLayoutFile(file.type)
                              }
                            />
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => deleteLayoutFile(file.type)}
                            />
                          </Space>
                        </Row>
                        <Row>
                          {uploadedFile?.creationDate && (
                            <div style={{ color: "#187056ff" }}>
                              {`Subido ${dayjs(uploadedFile.creationDate).from(
                                currentTime
                              )}`}
                            </div>
                          )}
                        </Row>
                      </div>
                    ) : (
                      file.description
                    )
                  }
                  labelAlign="right"
                  required
                >
                  <Spin
                    spinning={uploadingStates[file.type]}
                    indicator={
                      <LoadingOutlined style={{ fontSize: 24 }} spin />
                    }
                  >
                    <Dragger
                      accept=".csv"
                      customRequest={(options) =>
                        handleFileUpload(options, file.type)
                      }
                      multiple={false}
                      maxCount={1}
                      beforeUpload={validateFileBeforeUpload}
                      showUploadList={false}
                      disabled={uploadingStates[file.type]}
                      style={{
                        borderColor: isUploaded ? "#52c41a" : "#d9d9d9",
                        backgroundColor: isUploaded ? "#f6ffed" : "inherit",
                      }}
                    >
                      <p className="ant-upload-drag-icon">
                        {uploadingStates[file.type] ? (
                          <LoadingOutlined />
                        ) : isUploaded ? (
                          <FileExcelOutlined />
                        ) : (
                          <UploadOutlined />
                        )}
                      </p>
                      <p className="ant-upload-text">
                        {uploadingStates[file.type]
                          ? `Subiendo archivo ${file.label}...`
                          : "Haga clic para cargar o arrastre y suelte"}
                      </p>
                      {isUploaded && (
                        <p className="ant-upload-hint">
                          Puedes subir una nueva versión
                        </p>
                      )}
                      <p className="ant-upload-hint">Solo archivos CSV</p>
                    </Dragger>
                  </Spin>
                </Form.Item>
              </ResponsiveCol>
            );
          })}
          <ResponsiveCol size="xl">
            <Button
              type="primary"
              style={{ float: "right" }}
              icon={<StepForwardOutlined />}
              disabled={!areAllFilesUploaded}
              onClick={() => executeValidationsAndContinue()}
            >
              Ejecutar validaciones y continuar
            </Button>
          </ResponsiveCol>
        </Row>
      )}
    </div>
  );
}

export default UploadStep;
